import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Input, Modal, Row, Select, Space, Spin, Tooltip, message } from 'antd';
import { EyeOutlined, PrinterOutlined, UploadOutlined, PlusOutlined, CloudUploadOutlined } from '@ant-design/icons';
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { AppapproDTO, ActionFlowRequestDTO } from '@/shared/api/model';
import { useCompany } from '@/hooks/company/use-company';
import { useAuthUser } from '@/modules/auth/context/AuthContext';
import { useLoanSchedule } from '@/hooks/loans/useLoans';
import { useLoanFeeCalculation } from '@/modules/loans/hooks/useLoanFeeCalculation';
import { useProductPropertiesById } from '@/modules/loan-product-settings/hooks/useProductProperties';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';
import { EmployerFormModal } from '@/modules/employers/components/EmployerFormModal';
import WebDeductionFormComponent, { OfficerDetails, FeeBreakdownData } from './WebDeductionFormComponent';
import { HAZINA_DEDUCTION_FORM_CONFIG } from './DeductionFormConfig';

interface OfficerDeductionFormGenerationViewProps {
    data?: ActionFlowRequestDTO;
    formikProps?: any;
}

const computeInitials = (fullName?: string) => {
    const name = (fullName || '').trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 1).toUpperCase();
    return `${parts[0][0].toUpperCase()}${parts[parts.length - 1][0].toUpperCase()}`;
};

/**
 * Used in the LOAN_OFFICER node — allows the officer to fill their details, preview, download,
 * and upload the official deduction form before sending the request to the member.
 */
const OfficerDeductionFormGenerationView: React.FC<OfficerDeductionFormGenerationViewProps> = ({
    data,
    formikProps,
}) => {
    const [form] = Form.useForm();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingOnFly, setUploadingOnFly] = useState(false);
    const [uploadedFormId, setUploadedFormId] = useState<string | null>(null);
    const [formRenderKey, setFormRenderKey] = useState(0);

    // Employer search state (mirrors OfficerReviewDetailView)
    const [employerQuery, setEmployerQuery] = useState<string>('');
    const [employerOptions, setEmployerOptions] = useState<Array<{ label: string; value: number }>>([]);
    const [employerById, setEmployerById] = useState<Record<number, any>>({});
    const [isEmployerLoading, setIsEmployerLoading] = useState(false);
    const [selectedEmployerId, setSelectedEmployerId] = useState<number | undefined>(undefined);
    const [createEmployerModalOpen, setCreateEmployerModalOpen] = useState(false);

    const printableRef = useRef<HTMLDivElement>(null);

    const values = formikProps?.values || data || {};
    const valueData: AppapproDTO = values.valueData || {};
    const glNo = valueData.glNo || '';
    const loanAmount = Number(valueData.recommendedLoanAmt || valueData.loanAmount || 0);
    const loanPeriod = Number(valueData.recommendedLnPeriod || valueData.lnPeriod || 0);
    const productName = valueData.productName || '';

    const authUser = useAuthUser();
    const { data: companyData, isLoading: isCompanyLoading } = useCompany();
    const [{ data: scheduleData, isLoading: isScheduleLoading }] = useLoanSchedule(valueData as AppapproDTO);
    const { data: feesResponse, isLoading: isFeesLoading } = useLoanFeeCalculation(glNo, loanAmount, loanPeriod);
    const { data: productPropertiesResponse } = useProductPropertiesById(glNo || undefined);
    const installmentRoundingPrecision = (productPropertiesResponse?.data as any)?.installmentRoundingPrecision ?? null;
    const deductionFormConfig = useMemo(() => {
        const rawConfig = (productPropertiesResponse?.data as any)?.loanFormConfig;

        if (!rawConfig) {
            return HAZINA_DEDUCTION_FORM_CONFIG;
        }

        try {
            const parsedConfig = JSON.parse(rawConfig);
            return parsedConfig?.deductionForm || parsedConfig;
        } catch {
            return HAZINA_DEDUCTION_FORM_CONFIG;
        }
    }, [productPropertiesResponse?.data]);

    const isReady = !isCompanyLoading && !isScheduleLoading && !isFeesLoading;

    // Check if form has all required fields filled
    const isFormValid = () => {
        const fv = form.getFieldsValue();
        return !!(fv.checkNumber && fv.cCode);
    };

    // Employer search effect (same debounce pattern as OfficerReviewDetailView)
    useEffect(() => {
        if (!employerQuery || employerQuery.trim().length < 2) {
            setEmployerOptions([]);
            return;
        }
        const handle = setTimeout(async () => {
            setIsEmployerLoading(true);
            try {
                const response = await AXIOS_INSTANCE.get('/frontend/v1/employers', {
                    params: { q: employerQuery.trim(), limit: 10 },
                });
                const employers = response?.data?.data || [];
                const nextById: Record<number, any> = {};
                const nextOptions = (employers as any[]).map((e) => {
                    nextById[e.id] = e;
                    return { label: e.voteNumber ? `${e.name} (${e.voteNumber})` : e.name, value: e.id };
                });
                setEmployerById((prev) => ({ ...prev, ...nextById }));
                setEmployerOptions(nextOptions);
            } catch {
                // ignore
            } finally {
                setIsEmployerLoading(false);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [employerQuery]);

    // Pre-populate employer if already linked on valueData
    useEffect(() => {
        const existingEmployerId = (valueData as any)?.employerId as number | undefined;
        if (!selectedEmployerId && existingEmployerId) {
            setSelectedEmployerId(existingEmployerId);
            AXIOS_INSTANCE.get(`/frontend/v1/employers/${existingEmployerId}`)
                .then((res) => {
                    const emp = res?.data?.data;
                    if (emp?.id) {
                        setEmployerById((prev) => ({ ...prev, [emp.id]: emp }));
                        form.setFieldsValue({
                            employerAddress: emp.address,
                            voteNumber: emp.voteNumber,
                        });
                    }
                })
                .catch(() => {/* ignore */});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Seed officer fields from authUser / valueData on mount
    useEffect(() => {
        form.setFieldsValue({
            officerName: (valueData as any)?.officerName || (authUser as any)?.fullname || '',
            officerPhone: (valueData as any)?.officerPhone || (authUser as any)?.phoneNo || '',
            officerInitials: (valueData as any)?.officerInitials || computeInitials((authUser as any)?.fullname),
            checkNumber: valueData.checkNumber || valueData.empId || '',
            cCode: (valueData as any)?.cCode || '',
            voteNumber: valueData.voteNumber || '',
            employerAddress: valueData.employerAddress || '',
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedEmployer = useMemo(() => {
        if (!selectedEmployerId) return undefined;
        return employerById[selectedEmployerId];
    }, [employerById, selectedEmployerId]);

    const mergedEmployerOptions = useMemo(() => {
        const map = new Map<number, { label: string; value: number }>();
        employerOptions.forEach((o) => map.set(o.value, o));
        if (selectedEmployer) {
            map.set(selectedEmployer.id, {
                value: selectedEmployer.id,
                label: selectedEmployer.voteNumber
                    ? `${selectedEmployer.name} (${selectedEmployer.voteNumber})`
                    : selectedEmployer.name,
            });
        }
        return Array.from(map.values());
    }, [employerOptions, selectedEmployer]);

    const handleEmployerSelect = (id: number) => {
        setSelectedEmployerId(id);
        const emp = employerById[id];
        if (emp) {
            form.setFieldsValue({
                employerAddress: emp.address || '',
                voteNumber: emp.voteNumber || '',
            });
            // Propagate to formik so form preview is live
            setFormRenderKey((k) => k + 1);
        }
    };

    const feeBreakdown: FeeBreakdownData | undefined = feesResponse?.data
        ? {
            disbursementFees: (feesResponse.data.disbursementFees || []).map((f: any) => ({
                feeName: f.feeName,
                calculatedAmount: f.calculatedAmount,
            })),
            repaymentFees: (feesResponse.data.repaymentFees || []).map((f: any) => ({
                feeName: f.feeName,
                calculatedAmount: f.calculatedAmount,
            })),
            totalDisbursementFees: feesResponse.data.totalDisbursementFees,
            totalRepaymentFees: feesResponse.data.totalRepaymentFees,
            netDisbursementAmount: feesResponse.data.netDisbursementAmount,
            hasDisbursementFees: feesResponse.data.hasDisbursementFees,
            hasRepaymentFees: feesResponse.data.hasRepaymentFees,
        }
        : undefined;

    const getOfficerDetails = (): OfficerDetails => {
        const fv = form.getFieldsValue();
        return {
            officerName: fv.officerName || (authUser as any)?.fullname || '',
            officerInitials: fv.officerInitials || '',
            officerPhone: fv.officerPhone || (authUser as any)?.phoneNo || '',
        };
    };

    const getLiveValueData = (): AppapproDTO => {
        const fv = form.getFieldsValue();
        return {
            ...valueData,
            checkNumber: fv.checkNumber || valueData.checkNumber || '',
            cCode: fv.cCode || (valueData as any)?.cCode || '',
            employerAddress: fv.employerAddress || valueData.employerAddress || '',
            voteNumber: fv.voteNumber || valueData.voteNumber || '',
        } as AppapproDTO;
    };

    const syncWorkflowValueData = () => {
        if (!formikProps?.setFieldValue) {
            return;
        }

        const fv = form.getFieldsValue();
        formikProps.setFieldValue('valueData.checkNumber', fv.checkNumber || valueData.checkNumber || '');
        formikProps.setFieldValue('valueData.cCode', fv.cCode || (valueData as any)?.cCode || '');
        formikProps.setFieldValue('valueData.officerName', fv.officerName || (valueData as any)?.officerName || '');
        formikProps.setFieldValue('valueData.officerInitials', fv.officerInitials || (valueData as any)?.officerInitials || '');
        formikProps.setFieldValue('valueData.officerPhone', fv.officerPhone || (valueData as any)?.officerPhone || '');
        formikProps.setFieldValue('valueData.employerAddress', fv.employerAddress || valueData.employerAddress || '');
        formikProps.setFieldValue('valueData.voteNumber', fv.voteNumber || valueData.voteNumber || '');
    };

    // Sync validation errors to formik to prevent flow navigation when required fields are missing
    useEffect(() => {
        if (!formikProps?.setFieldError) return;
        
        const fv = form.getFieldsValue();
        if (!fv.checkNumber) {
            formikProps.setFieldError('valueData.checkNumber', 'Check Number is required');
        } else {
            formikProps.setFieldError('valueData.checkNumber', undefined);
        }
        
        if (!fv.cCode) {
            formikProps.setFieldError('valueData.cCode', 'C-CODE is required');
        } else {
            formikProps.setFieldError('valueData.cCode', undefined);
        }
    }, [formRenderKey, formikProps]);

    const handlePrintRef = useReactToPrint({
        content: () => printableRef.current,
        documentTitle: `Deduction_Form_${valueData.custNo || 'draft'}_${Date.now()}`,
        pageStyle: `@page { size: A4; margin: 10mm; }`,
    });

    const handlePreview = () => {
        if (!isFormValid()) {
            message.error('Please fill all required fields (C-CODE, Check Number) before previewing');
            form.validateFields();
            return;
        }
        setIsPreviewOpen(true);
    };

    const handlePrint = () => {
        if (!isFormValid()) {
            message.error('Please fill all required fields (C-CODE, Check Number) before printing');
            form.validateFields();
            return;
        }
        handlePrintRef();
    };

    const buildPdfBlob = async (): Promise<Blob> => {
        if (!printableRef.current) throw new Error('Form ref not ready');
        const canvas = await html2canvas(printableRef.current, {
            scale: 1,
            useCORS: true,
            backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgHeight = (canvas.height * pageWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft > 0) {
            position -= pageHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
            heightLeft -= pageHeight;
        }
        return pdf.output('blob');
    };

    const persistUpload = async (file: File | Blob, fileName: string) => {
        const formData = new FormData();
        formData.append('file', file, fileName);
        const metadata = {
            category: 'official_deduction_form',
            domain: 'LOAN_APPLICATION',
            domainId: (valueData.branch || '') + '.' + (valueData.loanId || valueData.id || ''),
            role: 'official_deduction_form',
            documentType: 'OFFICIAL_DEDUCTION_FORM',
            description: `Official Deduction Form for ${valueData.name || ''}`,
        };
        formData.append('metadata', JSON.stringify(metadata));
        const response = await AXIOS_INSTANCE.post('/api/files/upload', formData);
        const fileId = (response as any)?.data?.id;
        setUploadedFormId(fileId || null);
        if (formikProps?.setFieldValue && fileId) {
            const currentAttachments = formikProps.values.valueData?.attachments || [];
            formikProps.setFieldValue('valueData.attachments', [
                ...currentAttachments,
                {
                    id: fileId,
                    fileName,
                    role: 'official_deduction_form',
                    documentType: 'OFFICIAL_DEDUCTION_FORM',
                    uploadDate: new Date().toISOString(),
                },
            ]);
            syncWorkflowValueData();
        }
    };

    /** Upload a file chosen by the user (signed form) */
    const handleUploadFile = async (file: File) => {
        if (!isFormValid()) {
            message.error('Please fill all required fields (C-CODE, Check Number) before uploading');
            form.validateFields();
            return;
        }
        setUploading(true);
        try {
            await persistUpload(file, file.name);
            message.success('Official deduction form uploaded successfully');
        } catch {
            message.error('Failed to upload the deduction form');
        } finally {
            setUploading(false);
        }
    };

    /** Generate PDF from the current form state and upload directly — no download needed */
    const handleUploadOnFly = async () => {
        if (!isFormValid()) {
            message.error('Please fill all required fields (C-CODE, Check Number) before uploading');
            form.validateFields();
            return;
        }
        setUploadingOnFly(true);
        try {
            setFormRenderKey((k) => k + 1); // ensure latest values are rendered
            await new Promise((res) => setTimeout(res, 200)); // let DOM settle
            const blob = await buildPdfBlob();
            const fileName = `official_deduction_form_${valueData.custNo || 'draft'}_${Date.now()}.pdf`;
            await persistUpload(blob, fileName);
            message.success('Deduction form generated and uploaded successfully');
        } catch {
            message.error('Failed to generate and upload the deduction form');
        } finally {
            setUploadingOnFly(false);
        }
    };

    const liveValueData = getLiveValueData();
    const officerDetails = getOfficerDetails();
    const liveCCode = form.getFieldValue('cCode') || (valueData as any)?.cCode || '';

    return (
        <Card title="Deduction Form — Officer Preparation" style={{ marginBottom: 16 }}>
            <Alert
                message="Officer Action Required"
                description="Fill in your details and employer information below, then generate the official deduction form. Use 'Upload Deduction Form' to generate and upload directly without downloading."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Form
                form={form}
                layout="vertical"
                onValuesChange={() => {
                    setFormRenderKey((k) => k + 1);
                    syncWorkflowValueData();
                }}
            >
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item label="Officer Name" name="officerName" rules={[{ required: true, message: 'Please enter your name' }]}>
                            <Input placeholder="e.g. JOHN MWAMBA" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                        <Form.Item label="Initials" name="officerInitials">
                            <Input placeholder="e.g. J.M." />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item label="Officer Phone" name="officerPhone">
                            <Input placeholder="e.g. 0712 345 678" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={6}>
                        <Form.Item
                            label="Check Number / Employee ID"
                            name="checkNumber"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Input placeholder="Employee check number" />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Employer Section */}
                <Row gutter={16} align="bottom">
                    <Col xs={24} md={12}>
                        <Form.Item label="Employer (search by name or vote number)">
                            <Select
                                showSearch
                                filterOption={false}
                                placeholder="Search employer..."
                                loading={isEmployerLoading}
                                value={selectedEmployerId}
                                onSearch={setEmployerQuery}
                                onChange={handleEmployerSelect}
                                options={mergedEmployerOptions}
                                allowClear
                                onClear={() => {
                                    setSelectedEmployerId(undefined);
                                    form.setFieldsValue({ employerAddress: '', voteNumber: '' });
                                }}
                                notFoundContent={
                                    employerQuery.length >= 2
                                        ? isEmployerLoading ? <Spin size="small" /> : 'No employers found'
                                        : 'Type at least 2 characters'
                                }
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                        <Form.Item label=" ">
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => setCreateEmployerModalOpen(true)}
                            >
                                New Employer
                            </Button>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                        <Form.Item label="Vote Number" name="voteNumber">
                            <Input placeholder="Vote number" readOnly={!!selectedEmployer} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={4}>
                        <Form.Item
                            label={
                                <Tooltip title="Cost Centre Code — must match employer payroll system">
                                    C-CODE <span style={{ color: 'red' }}>*</span>
                                </Tooltip>
                            }
                            name="cCode"
                            rules={[{ required: true, message: 'C-CODE is required' }]}
                        >
                            <Input placeholder="e.g. FC7793" style={{ fontWeight: 'bold' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item label="Employer Address" name="employerAddress">
                    <Input.TextArea rows={3} placeholder="Employer full address (one line per row)" />
                </Form.Item>
            </Form>

            {!isReady && (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin tip="Loading loan data..." />
                </div>
            )}

            {isReady && (
                <Space wrap style={{ marginTop: 8 }}>
                    <span>Preview</span>
                    <Button 
                        icon={<EyeOutlined />} 
                        onClick={handlePreview}
                        disabled={!isFormValid()}
                    >
                        Preview Form
                    </Button>

                    <span>Print</span>
                    <Button 
                        icon={<PrinterOutlined />} 
                        onClick={handlePrint}
                        disabled={!isFormValid()}
                    >
                        Print Form
                    </Button>

                    <span>Upload</span>
                    <Tooltip title="Generates the deduction form from what you see and uploads it directly — no download needed">
                        <Button
                            type="primary"
                            icon={<CloudUploadOutlined />}
                            loading={uploadingOnFly}
                            onClick={handleUploadOnFly}
                            disabled={!!uploadedFormId || !isFormValid()}
                        >
                            {uploadedFormId ? 'Form Uploaded ✓' : 'Upload Deduction Form'}
                        </Button>
                    </Tooltip>

                    <Button
                        icon={<UploadOutlined />}
                        loading={uploading}
                        onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf,.jpg,.jpeg,.png';
                            input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement).files?.[0];
                                if (file) handleUploadFile(file);
                            };
                            input.click();
                        }}
                        disabled={!!uploadedFormId || !isFormValid()}
                    >
                        Upload Signed Form
                    </Button>
                </Space>
            )}

            {uploadedFormId && (
                <Alert
                    message="Official form uploaded successfully"
                    description="The member will now be able to download this form, get it signed by their employer, and upload the signed copy."
                    type="success"
                    showIcon
                    style={{ marginTop: 16 }}
                />
            )}

            {/* Hidden form for printing/PDF generation */}
            {isReady && (
                <div style={{ position: 'fixed', left: -9999, top: 0, pointerEvents: 'none' }}>
                    <div ref={printableRef} key={formRenderKey}>
                        <WebDeductionFormComponent
                            user={authUser}
                            company={companyData}
                            product={{ productName }}
                            valueData={liveValueData}
                            scheduleData={scheduleData}
                            deductionFormConfig={deductionFormConfig}
                            officerDetails={officerDetails}
                            feeBreakdown={feeBreakdown}
                            installmentRoundingPrecision={installmentRoundingPrecision}
                            cCode={liveCCode}
                        />
                    </div>
                </div>
            )}

            {/* Preview modal */}
            <Modal
                title="Deduction Form Preview"
                open={isPreviewOpen}
                onCancel={() => setIsPreviewOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsPreviewOpen(false)}>Close</Button>,
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                        Print
                    </Button>,
                ]}
                width={900}
                styles={{ body: { padding: '20px', maxHeight: '80vh', overflowY: 'auto' } }}
            >
                <WebDeductionFormComponent
                    user={authUser}
                    company={companyData}
                    product={{ productName }}
                    valueData={liveValueData}
                    scheduleData={scheduleData}
                    deductionFormConfig={deductionFormConfig}
                    officerDetails={getOfficerDetails()}
                    feeBreakdown={feeBreakdown}
                    installmentRoundingPrecision={installmentRoundingPrecision}
                    cCode={liveCCode}
                />
            </Modal>

            {/* Create employer on-the-fly */}
            <EmployerFormModal
                open={createEmployerModalOpen}
                onClose={() => setCreateEmployerModalOpen(false)}
                onSaved={() => {
                    setCreateEmployerModalOpen(false);
                    setEmployerQuery('');
                }}
            />
        </Card>
    );
};

export default OfficerDeductionFormGenerationView;
